// The answer language of one chat turn: the language of the question (MP-OS-193).
//
// A port of app/api/chat_language.py, used for the errors the client writes itself
// (HTTP 429/403/other, a dropped stream). Both run on the same vectors
// (chatLanguage.cases.json), so the two implementations cannot drift. Keep the word
// lists identical to the Python module.

export type ChatLang = 'sv' | 'en'

const words = (text: string) => new Set(text.trim().split(/\s+/))

const SV_WORDS = words(`
  hur vad vilka vilken vilket varför när var vem många mycket mest minst bäst bästa
  största minsta sålde sålt säljer sälja såld försäljning försäljningen försäljningar
  månad månaden månader månads vecka veckan veckor år året åren dag dagen dagar idag
  igår ifjol fjol förra senaste nästa denna detta dessa och eller är var blev har hade
  visa visar jämför jämfört jämförelse med för från till mot utan på av om inte ja nej
  också samt än den det de som kan ska skulle vill får gör göra ge mig oss vi våra vår
  vårt du ni er alla hela totalt andel andelen marknadsandel marknadsandelar märke
  märken märket smak smaker stad städer staden tillverkare artikel artiklar förpackningar
  dosor burkar kunder ordrar topp ökade minskade ökning minskning
`)
const EN_WORDS = words(`
  how what which who why when where much many most least best biggest largest smallest
  did does do is are was were be been has have had the a an of and or in on for from
  to by with without at than show compare comparison sell sells sold selling sales
  month months monthly week weeks weekly year years yearly day days daily today
  yesterday last next this these those our we us me my you your can could would should
  please give list top share market brands flavour flavours flavor flavors city cities
  manufacturer manufacturers article articles packages customers orders increase
  decrease grew growth versus between
`)
const PLACE_NAMES = words(`
  göteborg malmö västerås örebro linköping norrköping jönköping umeå gävle borås
  södertälje växjö luleå sundsvall östersund
`)

const WORD = /\p{L}+(?:['-]\p{L}+)*/gu
const SWEDISH_LETTER = /[åäöÅÄÖ]/

// A catalogue or place name, or a place in the genitive ("Göteborgs"): data, not language.
function isData(word: string, data: Set<string>): boolean {
  return data.has(word) || PLACE_NAMES.has(word) || (word.endsWith('s') && PLACE_NAMES.has(word.slice(0, -1)))
}

// "i" is Swedish in lower case ("i år"). A capital "I" is the English pronoun, unless a
// capitalised name follows it ("I Malmö?"): the pronoun is followed by a verb.
function swedishI(raw: string, following: string | undefined): boolean {
  if (raw === 'i') return true
  if (!following || following[0] === following[0].toLowerCase()) return false
  const next = following.toLowerCase()
  return !EN_WORDS.has(next) && !SV_WORDS.has(next)
}

// A decomposed "Göteborg" (o + U+0308) would lose its Swedish letter.
const nfc = (text: unknown) => String(text ?? '').normalize('NFC')

function score(text: string, data: Set<string>): number {
  let total = 0
  const found = [...nfc(text).matchAll(WORD)].map((match) => match[0])
  found.forEach((raw, index) => {
    const word = raw.toLowerCase()
    if (isData(word, data)) return
    if (word === 'i') total += swedishI(raw, found[index + 1]) ? 2 : -2
    else if (SV_WORDS.has(word)) total += 2
    else if (EN_WORDS.has(word)) total -= 2
    else if (SWEDISH_LETTER.test(raw)) total += 1
  })
  return total
}

function vocabulary(data: Iterable<string>): Set<string> {
  const out = new Set<string>()
  for (const value of data) for (const match of nfc(value).matchAll(WORD)) out.add(match[0].toLowerCase())
  return out
}

export function detectLanguage(text: string, data: Iterable<string> = []): ChatLang | null {
  const value = score(text, vocabulary(data))
  return value > 0 ? 'sv' : value < 0 ? 'en' : null
}

interface Turn { role?: string; content?: unknown }

// An ambiguous question ("ZYN?") follows the previous user turn, then English.
export function chatLanguage(question: string, history: Turn[] = [], data: Iterable<string> = []): ChatLang {
  const names = [...data]
  const own = detectLanguage(question, names)
  if (own) return own
  for (let index = history.length - 1; index >= 0; index -= 1) {
    const turn = history[index]
    if (turn?.role === 'user' && typeof turn.content === 'string') return detectLanguage(turn.content, names) ?? 'en'
  }
  return 'en'
}
